/**
 * Detailed Project Verification
 * This script provides more detailed analysis of project-customer relationships
 */

import { directSupabase } from '../lib/supabaseClient';

export async function detailedProjectVerification() {
  console.log('🔍 Starting detailed project verification...');
  
  try {
    // Step 1: Get all projects with their customer_id values
    console.log('📊 Fetching all projects...');
    const { data: projects, error: projectError } = await directSupabase
      .from('projects')
      .select('id, project_name, customer_id, customer_name, customer_email')
      .limit(50);
    
    if (projectError) {
      throw new Error(`Failed to fetch projects: ${projectError.message}`);
    }
    
    console.log(`Found ${projects?.length || 0} projects`);
    
    // Step 2: Analyze customer_id types and values
    const analysis = {
      total: projects?.length || 0,
      null: 0,
      uuid: 0,
      integer: 0,
      string: 0,
      samples: [] as any[]
    };
    
    projects?.forEach((project, index) => {
      if (project.customer_id === null) {
        analysis.null++;
      } else if (typeof project.customer_id === 'string') {
        if (project.customer_id.length === 36 && project.customer_id.includes('-')) {
          analysis.uuid++;
        } else {
          analysis.string++;
        }
      } else if (typeof project.customer_id === 'number') {
        analysis.integer++;
      }
      
      // Keep first 10 samples for inspection
      if (index < 10) {
        analysis.samples.push({
          project_name: project.project_name,
          customer_id: project.customer_id,
          customer_id_type: typeof project.customer_id,
          customer_name: project.customer_name,
          customer_email: project.customer_email
        });
      }
    });
    
    console.log('📊 Project Customer ID Analysis:');
    console.table(analysis);
    console.log('📋 Sample Projects:');
    console.table(analysis.samples);
    
    // Step 3: Check migration mappings
    console.log('📊 Checking migration mappings...');
    const { data: mappings, error: mappingError } = await directSupabase
      .from('customer_migration_mapping')
      .select('*')
      .limit(10);
    
    if (mappingError) {
      console.error('❌ Failed to fetch mappings:', mappingError);
    } else {
      console.log(`Found ${mappings?.length || 0} migration mappings`);
      console.table(mappings);
    }
    
    // Step 4: Try to find specific matches between projects and mappings
    console.log('🔍 Looking for project-mapping matches...');
    const matches = [];
    
    if (projects && mappings) {
      for (const project of projects.slice(0, 10)) {
        if (project.customer_id !== null) {
          const mapping = mappings.find(m => 
            m.quotedock_customer_id === project.customer_id ||
            m.quotedock_customer_id.toString() === project.customer_id.toString()
          );
          
          if (mapping) {
            matches.push({
              project_name: project.project_name,
              project_customer_id: project.customer_id,
              quotedock_id: mapping.quotedock_customer_id,
              jarvis_uuid: mapping.jarvis_customer_id,
              customer_name: mapping.customer_name,
              match_type: typeof project.customer_id
            });
          }
        }
      }
    }
    
    if (matches.length > 0) {
      console.log('🎯 Found project-mapping matches:');
      console.table(matches);
    } else {
      console.log('❌ No matches found between projects and migration mappings');
    }
    
    // Step 5: Check if any projects were actually updated
    console.log('🔍 Checking for UUID customer_id values...');
    const { data: uuidProjects, error: uuidError } = await directSupabase
      .from('projects')
      .select('id, project_name, customer_id, customer_name')
      .not('customer_id', 'is', null);
    
    if (!uuidError && uuidProjects) {
      const actualUuids = uuidProjects.filter(p => 
        typeof p.customer_id === 'string' && 
        p.customer_id.length === 36 && 
        p.customer_id.includes('-')
      );
      
      console.log(`✅ Found ${actualUuids.length} projects with UUID customer_id`);
      if (actualUuids.length > 0) {
        console.table(actualUuids.slice(0, 5));
      }
    }
    
    return {
      success: true,
      analysis,
      mappingCount: mappings?.length || 0,
      matchCount: matches.length,
      matches
    };
    
  } catch (error) {
    console.error('❌ Detailed verification failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).detailedVerification = detailedProjectVerification;
















